<template>
  <div class="container pull-up">
    <div class="manage-program min-h-screen py-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" v-loading="loading">
        <!-- Program Header -->
        <div v-if="program" class="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg mb-6 overflow-hidden">
          <div class="px-8 py-6 text-white">
            <div class="flex items-center justify-between">
              <div>
                <h1 class="text-3xl font-bold mb-2">{{ program.name }}</h1>
                <p class="text-blue-100 mb-4">{{ program.description }}</p>
                <div class="flex items-center space-x-4">
                  <span
                    :class="program.is_active ? 'bg-green-500' : 'bg-gray-500'"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white">
                    {{ program.is_active ? $t('loyalty.status.active') : $t('loyalty.status.inactive') }}
                  </span>
                  <span class="text-blue-100">
                    {{ program.points_per_currency }} {{ $t('loyalty.label.points_per_currency') }} / {{ program.currency_code }}
                  </span>
                </div>
              </div>
              <!-- <button
                @click="editProgram"
                class="inline-flex items-center px-4 py-2 bg-primary bg-opacity-20 hover:bg-opacity-30 text-white font-medium rounded-lg transition-all duration-200 backdrop-blur-sm">
                <i class="mdi mdi-pencil mr-2"></i>
                {{ $t('button.edit') }}
              </button> -->
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200">
          <el-tabs v-model="activeTab" type="border-card" class="loyalty-tabs">
            <el-tab-pane :label="$t('loyalty.title.tiers')" name="tiers">
              <tier-manager
                ref="tierManager"
                :program-id="programId"
                @tier-created="handleTierCreated"
                @tier-updated="handleTierUpdated"
                @tier-deleted="handleTierDeleted">
              </tier-manager>
            </el-tab-pane>

            <el-tab-pane :label="$t('loyalty.title.rewards')" name="rewards">
              <reward-catalog
                ref="rewardCatalog"
                :program-id="programId"
                @reward-created="handleRewardCreated"
                @reward-updated="handleRewardUpdated"
                @reward-deleted="handleRewardDeleted">
              </reward-catalog>
            </el-tab-pane>

            <el-tab-pane :label="$t('loyalty.title.rules')" name="rules">
              <earning-rule-manager
                ref="earningRuleManager"
                :program-id="programId"
                @rule-created="handleRuleCreated"
                @rule-updated="handleRuleUpdated"
                @rule-deleted="handleRuleDeleted">
              </earning-rule-manager>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>

      <!-- Edit Program Dialog -->
      <loyalty-program-form
        :visible="showEditForm"
        :program="program"
        @saved="handleProgramUpdated"
        @cancelled="showEditForm = false">
      </loyalty-program-form>
    </div>
  </div>
</template>

<script>
import TierManager from './TierManager.vue'
import RewardCatalog from './RewardCatalog.vue'
import EarningRuleManager from './EarningRuleManager.vue'
import LoyaltyProgramForm from './LoyaltyProgramForm.vue'

export default {
  name: 'ManageProgram',
  components: {
    TierManager,
    RewardCatalog,
    EarningRuleManager,
    LoyaltyProgramForm
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      program: null,
      activeTab: 'tiers',
      loading: false,
      showEditForm: false
    }
  },
  computed: {
    programId () {
      return parseInt(this.id)
    }
  },
  async mounted () {
    this.setParentHeaderAction()
    await this.fetchProgram()
    this.$emit('parent-title', this.program ? this.program.name : this.$t('loyalty.title.manage', { name: '' }))
  },
  beforeDestroy () {
    this.$emit('clear-header-action')
  },
  methods: {
    setParentHeaderAction () {
      this.$emit('set-header-action', {
        label: this.$t('button.edit'),
        icon: 'mdi mdi-pencil',
        onClick: () => this.editProgram()
      })
    },
    async fetchProgram () {
      try {
        this.loading = true
        this.program = await this.$store.dispatch('loyalty/programs/fetchProgram', this.programId)
      } catch (error) {
        console.error('Error fetching program:', error)
        this.$router.push({ name: 'loyalty-programs' })
      } finally {
        this.loading = false
      }
    },
    editProgram () {
      this.showEditForm = true
    },
    async handleProgramUpdated () {
      this.showEditForm = false
      await this.fetchProgram()
    },
    handleTierCreated () {
      this.$refs.tierManager?.fetchTiers()
    },
    handleTierUpdated () {
      this.$refs.tierManager?.fetchTiers()
    },
    handleTierDeleted () {
      this.$refs.tierManager?.fetchTiers()
    },
    handleRewardCreated () {
      this.$refs.rewardCatalog?.fetchRewards()
    },
    handleRewardUpdated () {
      this.$refs.rewardCatalog?.fetchRewards()
    },
    handleRewardDeleted () {
      this.$refs.rewardCatalog?.fetchRewards()
    },
    handleRuleCreated () {
      this.$refs.earningRuleManager?.fetchRules()
    },
    handleRuleUpdated () {
      this.$refs.earningRuleManager?.fetchRules()
    },
    handleRuleDeleted () {
      this.$refs.earningRuleManager?.fetchRules()
    }
  }
}
</script>

<style scoped>
.loyalty-tabs ::v-deep .el-tabs__header {
  margin: 0;
  border-bottom: 1px solid #e5e7eb;
}

.loyalty-tabs ::v-deep .el-tabs__item {
  font-weight: 500;
  color: #6b7280;
}

.loyalty-tabs ::v-deep .el-tabs__item.is-active {
  color: #3b82f6;
  font-weight: 600;
}

.loyalty-tabs ::v-deep .el-tabs__content {
  padding: 0;
}
</style>
